import { useCallback, useMemo, useState } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { ARTICLE_REPORT_STATUSES, type ArticleReportStatusType, type ArticleReportType } from '@/defs/articleReport'
import { type Dict } from '@/defs/misc'
import { useModal } from '@/hooks/useModal'

import styles from './ArticleReport.module.scss'
import { ArticleReportModal } from './ArticleReportModal/ArticleReportModal'
import { ArticleReportReporter } from './ArticleReportReporter/ArticleReportReporter'
import { ArticleReportStatus } from './ArticleReportStatus/ArticleReportStatus'

type Props = {
  reports: ArticleReportType[]
}

export const ArticleReport = ({ reports }: Props) => {
  const { open } = useModal('article-report')

  const recReports = useMemo(
    () => reports.map((report, number) => ({ number, report })).filter((r) => !!r.report.rec),
    [reports],
  )

  const [reportMap, setReportMap] = useState(() =>
    reports.reduce<Dict<ArticleReportType>>((acc, report, i) => {
      acc[String(i)] = report
      return acc
    }, {}),
  )

  const reportsUpdated = useMemo(() => Object.values(reportMap).filter((r): r is ArticleReportType => !!r), [reportMap])

  const onResolve = useCallback((number: number, report: ArticleReportType) => {
    setReportMap((r) => ({ ...r, [number]: report }))
  }, [])

  const reportTotals = useMemo(() => {
    const reportTotals = ARTICLE_REPORT_STATUSES.reduce(
      (acc, status) => {
        acc[status] = 0
        return acc
      },
      {} as Record<ArticleReportStatusType, number>,
    )

    Object.values(reportMap).forEach((report) => {
      if (!report) return
      const { status } = report
      reportTotals[status] += 1
    })

    return reportTotals
  }, [reportMap])

  const loading = !!reportTotals.cardLoading

  const onClick = () => {
    open({ content: <ArticleReportModal reports={reportsUpdated} />, size: 'lg', width: 'full' })
  }

  return (
    <>
      {recReports.map(({ number, report }) => (
        <ArticleReportReporter key={number} number={number} onResolve={onResolve} report={report} />
      ))}
      <ButtonRoot className={styles.container} disabled={loading} onClick={onClick}>
        <span className={styles.heading}>Preview Report Summary</span>
        <span className={styles.statuses}>
          {ARTICLE_REPORT_STATUSES.map((status) => (
            <ArticleReportStatus key={status} status={status} total={reportTotals[status]} />
          ))}
        </span>
      </ButtonRoot>
    </>
  )
}
